import { supabase } from '../../../shared/services/supabase';

export interface Manufacturer {
  id: string;
  name: string;
  brands: string[] | null;
  min_order_qty: number | null;
  min_order_amt: number | null;
  free_ship_amt: number | null;
  contact_phone: string | null;
  contact_email: string | null;
  rep_name: string | null;
  rep_phone: string | null;
  notes: string | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateManufacturerInput {
  name: string;
  brands?: string[];
  min_order_qty?: number;
  min_order_amt?: number;
  free_ship_amt?: number;
  contact_phone?: string;
  contact_email?: string;
  rep_name?: string;
  rep_phone?: string;
  notes?: string;
  is_active?: boolean;
}

export interface UpdateManufacturerInput {
  id: string;
  name?: string;
  brands?: string[];
  min_order_qty?: number | null;
  min_order_amt?: number | null;
  free_ship_amt?: number | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  rep_name?: string | null;
  rep_phone?: string | null;
  notes?: string | null;
  is_active?: boolean;
}

export const manufacturersService = {
  /**
   * Fetch all manufacturers
   */
  async getManufacturers(): Promise<Manufacturer[]> {
    const { data, error } = await supabase
      .from('manufacturers')
      .select('*')
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching manufacturers:', error);
      throw new Error(error.message);
    }

    return data as Manufacturer[];
  },

  /**
   * Fetch active manufacturers only
   */
  async getActiveManufacturers(): Promise<Manufacturer[]> {
    const { data, error } = await supabase
      .from('manufacturers')
      .select('*')
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching active manufacturers:', error);
      throw new Error(error.message);
    }

    return data as Manufacturer[];
  },

  /**
   * Fetch a single manufacturer by ID
   */
  async getManufacturer(id: string): Promise<Manufacturer> {
    const { data, error } = await supabase
      .from('manufacturers')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching manufacturer:', error);
      throw new Error(error.message);
    }

    return data as Manufacturer;
  },

  /**
   * Create a new manufacturer
   */
  async createManufacturer(
    input: CreateManufacturerInput
  ): Promise<Manufacturer> {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;

    const { data, error } = await supabase
      .from('manufacturers')
      // @ts-expect-error - Database types not regenerated yet
      .insert({
        name: input.name,
        brands: input.brands ?? null,
        min_order_qty: input.min_order_qty ?? null,
        min_order_amt: input.min_order_amt ?? null,
        free_ship_amt: input.free_ship_amt ?? null,
        contact_phone: input.contact_phone ?? null,
        contact_email: input.contact_email ?? null,
        rep_name: input.rep_name ?? null,
        rep_phone: input.rep_phone ?? null,
        notes: input.notes ?? null,
        is_active: input.is_active ?? true,
        created_by: userId ?? null,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating manufacturer:', error);
      throw new Error(error.message);
    }

    return data as Manufacturer;
  },

  /**
   * Update an existing manufacturer
   */
  async updateManufacturer(
    input: UpdateManufacturerInput
  ): Promise<Manufacturer> {
    const updateData: Partial<Manufacturer> = {};

    if (input.name !== undefined) updateData.name = input.name;
    if (input.brands !== undefined) updateData.brands = input.brands;
    if (input.min_order_qty !== undefined)
      updateData.min_order_qty = input.min_order_qty;
    if (input.min_order_amt !== undefined)
      updateData.min_order_amt = input.min_order_amt;
    if (input.free_ship_amt !== undefined)
      updateData.free_ship_amt = input.free_ship_amt;
    if (input.contact_phone !== undefined)
      updateData.contact_phone = input.contact_phone;
    if (input.contact_email !== undefined)
      updateData.contact_email = input.contact_email;
    if (input.rep_name !== undefined) updateData.rep_name = input.rep_name;
    if (input.rep_phone !== undefined) updateData.rep_phone = input.rep_phone;
    if (input.notes !== undefined) updateData.notes = input.notes;
    if (input.is_active !== undefined) updateData.is_active = input.is_active;

    const { data, error } = await supabase
      .from('manufacturers')
      // @ts-expect-error - Supabase type mismatch
      .update(updateData)
      .eq('id', input.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating manufacturer:', error);
      throw new Error(error.message);
    }

    return data as Manufacturer;
  },

  /**
   * Delete a manufacturer (soft delete)
   */
  async deleteManufacturer(id: string): Promise<void> {
    const { error } = await supabase
      .from('manufacturers')
      // @ts-expect-error - Supabase type mismatch
      .update({ is_active: false })
      .eq('id', id);

    if (error) {
      console.error('Error deleting manufacturer:', error);
      throw new Error(error.message);
    }
  },

  /**
   * Activate a manufacturer
   */
  async activateManufacturer(id: string): Promise<Manufacturer> {
    return this.updateManufacturer({ id, is_active: true });
  },

  /**
   * Deactivate a manufacturer
   */
  async deactivateManufacturer(id: string): Promise<Manufacturer> {
    return this.updateManufacturer({ id, is_active: false });
  },
};
